import { useCallback, useEffect, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog } from "@/components/common";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, FilePdf, PencilSimple, CircleNotch } from "@phosphor-icons/react";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

const DOC_TYPES = [
  { value: "contract", label: "Contrato" },
  { value: "memorandum", label: "Memorándum" },
  { value: "attention", label: "Carta de atención" },
];
const typeLabel = (v) => DOC_TYPES.find((t) => t.value === v)?.label || v;

export default function Documentos() {
  const [docs, setDocs] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ employee_id: "", type: "contract", title: "", content: "" });
  const [tplOpen, setTplOpen] = useState(false);
  const [tplEditing, setTplEditing] = useState(null);
  const [tplForm, setTplForm] = useState({ name: "", type: "contract", content: "" });
  const [toDelete, setToDelete] = useState(null);
  const [tplDelete, setTplDelete] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/documents"), api.get("/document-templates"), api.get("/employees")])
      .then(([d, t, e]) => {
        setDocs(Array.isArray(d.data) ? d.data : []);
        setTemplates(Array.isArray(t.data) ? t.data : []);
        setEmployees((Array.isArray(e.data) ? e.data : []).filter((x) => x.active));
      })
      .catch((err) => {
        toast.error(apiError(err));
        setDocs([]);
      });
  }, []);

  useEffect(load, [load]);

  const generate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/documents", genForm);
      toast.success("Documento generado");
      setGenOpen(false);
      setGenForm({ employee_id: "", type: "contract", title: "", content: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (tplEditing) {
        await api.patch(`/document-templates/${tplEditing.id}`, tplForm);
        toast.success("Plantilla actualizada");
      } else {
        await api.post("/document-templates", tplForm);
        toast.success("Plantilla creada");
      }
      setTplOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeDoc = async () => {
    try {
      await api.delete(`/documents/${toDelete.id}`);
      toast.success("Documento eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeTpl = async () => {
    try {
      await api.delete(`/document-templates/${tplDelete.id}`);
      toast.success("Plantilla eliminada");
      setTplDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const bulkRemove = async () => {
    try {
      await api.post("/documents/bulk-delete", { ids: selected });
      toast.success(`${selected.length} documentos eliminados`);
      setSelected([]);
      setBulkConfirm(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const downloadDocPdf = async (d) => {
    try {
      const res = await localApi.get(`/document-tools/${d.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d.title || "documento").replace(/[^A-Za-z0-9_-]+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error(apiError(e, "No se pudo generar el PDF."));
    }
  };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (t) setGenForm((f) => ({ ...f, type: t.type, title: t.name, content: t.content }));
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (!docs) return <Loading label="Cargando documentos..." />;

  return (
    <div data-testid="documentos-page">
      <PageHeader title="Documentos" description="Genera constancias, contratos y memorándums con PDF" testid="documentos-header">
        {selected.length > 0 && (
          <Button variant="outline" className="rounded-sm border-red-900 text-red-400 hover:bg-red-950/40 hover:text-red-300" data-testid="bulk-delete-docs-button" onClick={() => setBulkConfirm(true)}>
            <Trash className="mr-2" size={15} /> Eliminar ({selected.length})
          </Button>
        )}
        <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="new-document-button" onClick={() => setGenOpen(true)}>
          <Plus className="mr-2" size={15} /> Generar documento
        </Button>
      </PageHeader>

      <Tabs defaultValue="docs">
        <TabsList className="rounded-sm bg-muted border border-border">
          <TabsTrigger value="docs" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-docs">Documentos</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-templates">Plantillas</TabsTrigger>
        </TabsList>

        <TabsContent value="docs" className="mt-4">
          {docs.length === 0 ? (
            <EmptyState title="Sin documentos" description="Genera tu primer documento a partir de una plantilla o desde cero." />
          ) : (
            <div className="border border-border rounded-sm overflow-x-auto bg-card">
              <table className="w-full text-sm" data-testid="documents-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                    <th className="p-3 w-10"></th>
                    <th className="p-3 font-normal">Título</th>
                    <th className="p-3 font-normal">Empleado</th>
                    <th className="p-3 font-normal">Tipo</th>
                    <th className="p-3 font-normal">Fecha</th>
                    <th className="p-3 font-normal text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {docs.map((d) => (
                    <tr key={d.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`doc-row-${d.id}`}>
                      <td className="p-3">
                        <Checkbox checked={selected.includes(d.id)} onCheckedChange={() => toggleSelect(d.id)} className="border-input" data-testid={`select-doc-${d.id}`} />
                      </td>
                      <td className="p-3 text-foreground font-medium">{d.title}</td>
                      <td className="p-3 text-foreground/80">{d.employee_name}</td>
                      <td className="p-3 text-foreground/80">{typeLabel(d.type)}</td>
                      <td className="p-3 text-muted-foreground">{fmtDate(d.created_at)}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`doc-pdf-${d.id}`}
                            onClick={() => downloadDocPdf(d)}>
                            <FilePdf size={15} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" data-testid={`doc-delete-${d.id}`} onClick={() => setToDelete(d)}>
                            <Trash size={15} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground" data-testid="new-template-button"
              onClick={() => { setTplEditing(null); setTplForm({ name: "", type: "contract", content: "" }); setTplOpen(true); }}>
              <Plus className="mr-2" size={15} /> Nueva plantilla
            </Button>
          </div>
          {templates.length === 0 ? (
            <EmptyState title="Sin plantillas" description="Crea plantillas reutilizables para generar documentos más rápido." />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="templates-grid">
              {templates.map((t) => (
                <div key={t.id} className="border border-border bg-card rounded-sm p-5 hover:border-foreground/40 transition-colors duration-200" data-testid={`template-card-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-heading text-base font-bold text-foreground">{t.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{typeLabel(t.type)}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`template-edit-${t.id}`}
                        onClick={() => { setTplEditing(t); setTplForm({ name: t.name, type: t.type, content: t.content }); setTplOpen(true); }}>
                        <PencilSimple size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" data-testid={`template-delete-${t.id}`} onClick={() => setTplDelete(t)}>
                        <Trash size={15} />
                      </Button>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground line-clamp-3">{t.content}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Generar documento</DialogTitle>
          </DialogHeader>
          <form onSubmit={generate} className="space-y-4" data-testid="document-form">
            {templates.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Usar plantilla</Label>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className={inputCls} data-testid="template-picker"><SelectValue placeholder="Seleccionar plantilla (opcional)" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleado *</Label>
              <Select value={genForm.employee_id} onValueChange={(v) => setGenForm({ ...genForm, employee_id: v })}>
                <SelectTrigger className={inputCls} data-testid="doc-employee-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</Label>
                <Select value={genForm.type} onValueChange={(v) => setGenForm({ ...genForm, type: v })}>
                  <SelectTrigger className={inputCls} data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Título *</Label>
                <Input className={inputCls} required data-testid="doc-title-input" value={genForm.title} onChange={(e) => setGenForm({ ...genForm, title: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contenido</Label>
              <Textarea className={`${inputCls} min-h-[160px]`} data-testid="doc-content-input" value={genForm.content} onChange={(e) => setGenForm({ ...genForm, content: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="doc-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Generar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{tplEditing ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTemplate} className="space-y-4" data-testid="template-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nombre *</Label>
                <Input className={inputCls} required data-testid="template-name-input" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</Label>
                <Select value={tplForm.type} onValueChange={(v) => setTplForm({ ...tplForm, type: v })}>
                  <SelectTrigger className={inputCls} data-testid="template-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contenido *</Label>
              <Textarea className={`${inputCls} min-h-[160px]`} required data-testid="template-content-input" value={tplForm.content} onChange={(e) => setTplForm({ ...tplForm, content: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="template-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={() => setToDelete(null)} title="Eliminar documento" description={`Se eliminará "${toDelete?.title}".`} onConfirm={removeDoc} testid="delete-doc" />
      <ConfirmDialog open={!!tplDelete} onOpenChange={() => setTplDelete(null)} title="Eliminar plantilla" description={`Se eliminará la plantilla "${tplDelete?.name}".`} onConfirm={removeTpl} testid="delete-template" />
      <ConfirmDialog open={bulkConfirm} onOpenChange={setBulkConfirm} title="Eliminación masiva" description={`Se eliminarán ${selected.length} documentos.`} onConfirm={bulkRemove} testid="bulk-delete-docs" />
    </div>
  );
}
